import React, { useMemo, useRef } from "react";
import styles from "./index.module.css";
import { Link, List, ListItem, ListItemIcon, ListItemText, Typography } from "@mui/material";
import i18nPackage from "../../i18n/i18n";
import { CorePopoverButton, CoreNewTabIcon, EllipsisTooltip, useFileUrl } from "../../core";
import { PROCTORING_OPTION_TEXT } from "../proctoring-options";
import ConfirmationBlock, { ConfirmationRowDescription } from "./blocks/ConfirmationBlock";
import { ProctoringOptionCategory } from "../proctoring-options/option-text/ProctoringOptionText";
import { categorizeProctoringOptions } from "./confirmationHelper";
import { ConditionalWrapper } from "../../wrapper";
import {
	isBypassStrategy,
	isHeaderStrategy,
	isLinkStrategy,
	isSubHeaderStrategy,
	isTextStrategy,
	SummaryItem,
	SummarySubOption,
} from "./groupProcessors";

const findListHeader = (subOptions: SummarySubOption[]): string | undefined => {
	const headerOption = subOptions.find((sub) => isHeaderStrategy(sub.strategy));
	if (isHeaderStrategy(headerOption?.strategy)) {
		return headerOption.strategy.text;
	}

	return undefined;
};

const getListItems = (subOptions: SummarySubOption[]): SummarySubOption[] => {
	return subOptions.filter(
		(sub) =>
			isTextStrategy(sub.strategy) ||
			isLinkStrategy(sub.strategy) ||
			isBypassStrategy(sub.strategy) ||
			isSubHeaderStrategy(sub.strategy),
	);
};

const renderSubOptionIcon = (subItem?: SummarySubOption, icon?: SummaryItem["icon"]) => {
	const { icon: subItemIcon, key } = subItem || {};

	const finalIcon = icon || subItemIcon;

	if (!finalIcon) return null;

	if (typeof finalIcon === "function" || typeof finalIcon === "object") {
		const CustomComponent = finalIcon as React.ComponentType<{ className?: string }>;
		return (
			<span aria-hidden="true">
				<CustomComponent className={styles.subOptionIcon} />
			</span>
		);
	}

	// Check raw HTML <svg> strings injections (Apps / Extensions)
	if (typeof finalIcon === "string") {
		const trimmedIcon = finalIcon.trim();

		try {
			if (/^(<\?xml.*\?>)?\s*<svg/i.test(trimmedIcon)) {
				return <div dangerouslySetInnerHTML={{ __html: trimmedIcon }} />;
			}
		} catch (e) {
			console.error("Error rendering SVG icon from string for subItem", subItem, e);
			return null;
		}
	}

	// Fallback to ProctoringOptionText
	const DefaultIcon = icon ?? (key ? PROCTORING_OPTION_TEXT[key]?.icon : null);

	if (!DefaultIcon) return null;

	return (
		<span aria-hidden="true">
			<DefaultIcon className={styles.subOptionIcon} />
		</span>
	);
};

const renderBoldSentence = (items: SummaryItem[], i18nSentenceKey: string) => {
	const itemLabels = items.map((i) => i.text);

	const formatter = new Intl.ListFormat(i18nPackage.language, {
		style: "long",
		type: "conjunction",
	});

	// This creates a plain text string list like: "Camera, Screen, and Audio"
	const standardTextList = formatter.format(itemLabels);

	// Maps text to react nodes so we can replace them later since Intl.ListFormat doesn't allow formatting on react nodes
	const elementsMap = new Map<string, React.ReactNode>();

	items.forEach((item, idx) => {
		elementsMap.set(item.text, <SentenceItemDropdown key={idx} item={item} />);
	});

	// replace regex special characters
	const escapedLabels = itemLabels.map((label) => label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));

	// Create a matching regex capturing group (e.g., /(Camera|Screen|Audio)/g)
	const labelRegex = new RegExp(`(${escapedLabels.join("|")})`, "g");

	const parts = standardTextList.split(labelRegex);
	const formattedListElements = parts.map((part) => {
		// Replace string placeholder with react component
		return elementsMap.has(part) ? elementsMap.get(part) : part;
	});

	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument
	const baseSentence = i18nPackage.t(i18nSentenceKey as any) as string;

	// Split the sentence at the "{{items}}" placeholder boundary
	const sentenceParts = baseSentence.split("{{items}}");

	return (
		<>
			{sentenceParts[0]}
			{formattedListElements}
			{sentenceParts[1]}
		</>
	);
};

type DropdownRowProps = {
	icon?: React.ReactNode;
	text: React.ReactNode;
	/**@default true */
	listStyle?: boolean;
	/**@default 0 Track nested depth for indentation spacing */
	depth?: number;
};

export function DropdownRow({ icon, text, listStyle = true, depth = 0 }: DropdownRowProps) {
	return (
		<ListItem
			className={`${styles.dropdownListItemRow} ${listStyle ? styles.listStyle : ""}`}
			disableGutters
			sx={{
				// Indent row by 16px (1rem) multiplied by its recursive depth
				pl: depth > 0 ? `${depth}rem !important` : 0,
			}}
		>
			{icon && <ListItemIcon sx={{ minWidth: "24px" }}>{icon}</ListItemIcon>}
			<ListItemText sx={{ marginTop: "0", marginBottom: "0" }}>{text}</ListItemText>
		</ListItem>
	);
}

function DropdownSubOptionItem({ subOption, depth }: { subOption: SummarySubOption; depth: number }) {
	if (isHeaderStrategy(subOption.strategy)) return null; // Skip header variant in list rendering since it's used as the title
	const RenderedIcon = renderSubOptionIcon(subOption);
	let useListStyle = isTextStrategy(subOption.strategy) ? (subOption.strategy.listStyle ?? true) : true;

	const renderText = () => {
		if (isLinkStrategy(subOption.strategy)) {
			const { href, ariaLabel } = subOption.strategy;
			return (
				<EllipsisTooltip entry={href} width={320} disablePortal={false}>
					<Link className={styles.listLink} href={href} target="_blank" aria-label={ariaLabel}>
						{href}
					</Link>
				</EllipsisTooltip>
			);
		}
		if (isTextStrategy(subOption.strategy)) {
			return <Typography>{subOption.strategy.text}</Typography>;
		}
		if (isBypassStrategy(subOption.strategy)) {
			return <Typography>{i18nPackage.t("editQuizPage.configurationSummaryListTSX.bypassMode")}</Typography>;
		}
		if (isSubHeaderStrategy(subOption.strategy)) {
			useListStyle = false;
			return <Typography className={styles.subHeader}>{subOption.strategy.text}</Typography>;
		}
		return null;
	};

	return (
		<>
			<DropdownRow icon={RenderedIcon} text={renderText()} listStyle={useListStyle} depth={depth} />
			{subOption.subOptions && subOption.subOptions.length > 0 && (
				<List disablePadding>
					{subOption.subOptions.map((nestedSubItem, idx) => (
						<DropdownSubOptionItem key={`${nestedSubItem.key}-${idx}`} subOption={nestedSubItem} depth={depth + 1} />
					))}
				</List>
			)}
		</>
	);
}

function DropdownListMenu({ subOptions }: { subOptions: SummarySubOption[] }) {
	return (
		<div className={styles.dropdownListScrollContainer} tabIndex={-1}>
			<List disablePadding>
				{subOptions.map((subOption, idx) => (
					<DropdownSubOptionItem key={`${subOption.key}-${idx}`} subOption={subOption} depth={0} />
				))}
			</List>
		</div>
	);
}

function SentenceItemDropdown({ item }: { item: SummaryItem }) {
	const anchorRef = useRef<HTMLSpanElement | null>(null);
	const subOptions = item.subOptions ?? [];
	const filteredSubOptions = getListItems(subOptions);

	const listTitle = findListHeader(subOptions) ?? item.text;
	const hasListItems = filteredSubOptions.length > 0;
	const fileUrl = useFileUrl(item.file);

	return (
		<span ref={anchorRef} className={styles.inlineKeywordGroup}>
			<ConditionalWrapper
				condition={hasListItems}
				whenTrue={(children) => (
					<CorePopoverButton
						title={listTitle}
						buttonText={children}
						tooltip={item.listTooltip}
						anchorEl={anchorRef.current}
						maxWidth={400}
						variant="text"
						slotProps={{
							button: {
								className: styles.sentenceDropdownButton,
							},
						}}
					>
						<DropdownListMenu subOptions={filteredSubOptions} />
					</CorePopoverButton>
				)}
			>
				{fileUrl ? (
					<Link
						href={fileUrl}
						target="_blank"
						rel="noopener noreferrer"
						className={styles.referenceMaterialLink}
					>
						<CoreNewTabIcon width={16} height={16} color="#0b6f85" />
						<strong>{item.text}</strong>
					</Link>
				) : (
					<strong>{item.text}</strong>
				)}
			</ConditionalWrapper>
		</span>
	);
}

function GridItemWithListPopover({ item }: { item: SummaryItem }) {
	const textAnchorRef = useRef<HTMLDivElement | null>(null);

	const IconComponent = item.icon;
	const subOptions = item.subOptions ?? [];

	// Find items that will be rendered in a list
	const listItems = getListItems(subOptions);
	const hasListItems = listItems.length > 0;

	const listTitle = findListHeader(subOptions) ?? item.text;

	return (
		<div className={styles.gridItem} ref={textAnchorRef}>
			{IconComponent && <IconComponent aria-hidden="true" className={styles.gridIcon} />}
			<ConditionalWrapper
				condition={hasListItems}
				whenTrue={(children) => (
					<CorePopoverButton
						title={listTitle}
						tooltip={item.listTooltip}
						anchorEl={() => textAnchorRef.current as Element}
						buttonText={children}
						variant="text"
						slotProps={{
							button: {
								className: styles.gridButton,
							},
							spanWrapperClassName: styles.gridButtonWrapper,
						}}
					>
						<DropdownListMenu subOptions={listItems} />
					</CorePopoverButton>
				)}
			>
				<ConfirmationRowDescription tooltipType={"overflow"} disabled={hasListItems}>
					{item.text}
				</ConfirmationRowDescription>
			</ConditionalWrapper>
		</div>
	);
}

type RenderableGroup = {
	categoryKey: ProctoringOptionCategory;
	title: string;
	layout: "sentence" | "grid";
	items: SummaryItem[];
	i18nSentenceKey?: string;
};

export type ConfigurationSummaryListProps = {
	summary: ReturnType<typeof categorizeProctoringOptions>;
	onEdit?: (category: ProctoringOptionCategory) => void;
	allowedCategories?: ProctoringOptionCategory[];
	showCategoriesWithNoItems?: boolean;
};

export default function ConfigurationSummaryList({
	summary,
	onEdit,
	allowedCategories,
	showCategoriesWithNoItems = true,
}: ConfigurationSummaryListProps) {
	const renderedGroups = useMemo((): RenderableGroup[] => {
		const groups: RenderableGroup[] = [];

		const shouldHideEmptyCategory = (items: SummaryItem[]) => !showCategoriesWithNoItems && items.length === 0;

		if (!allowedCategories || allowedCategories.includes("record")) {
			const items = summary.record ?? [];
			if (!shouldHideEmptyCategory(items)) {
				groups.push({
					categoryKey: "record",
					title: i18nPackage.t("editQuizPage.configurationSummaryListTSX.compact.record"),
					layout: "sentence",
					i18nSentenceKey: "editQuizPage.configurationSummaryListTSX.compact.recordingSummary",
					items: items,
				});
			}
		}

		if (!allowedCategories || allowedCategories.includes("verification")) {
			const items = summary.verification ?? [];
			if (!shouldHideEmptyCategory(items)) {
				groups.push({
					categoryKey: "verification",
					title: i18nPackage.t("editQuizPage.configurationSummaryListTSX.compact.verification"),
					layout: "sentence",
					i18nSentenceKey: "editQuizPage.configurationSummaryListTSX.compact.verificationSummary",
					items: items,
				});
			}
		}

		if (!allowedCategories || allowedCategories.includes("otherActivities")) {
			const items = summary.otherActivities ?? [];
			if (!shouldHideEmptyCategory(items)) {
				groups.push({
					categoryKey: "otherActivities",
					title: i18nPackage.t("editQuizPage.configurationSummaryListTSX.compact.additionalRestrictions"),
					layout: "grid",
					items: items,
				});
			}
		}

		if (!allowedCategories || allowedCategories.includes("showTools")) {
			const items = summary.showTools ?? [];
			if (!shouldHideEmptyCategory(items)) {
				groups.push({
					categoryKey: "showTools",
					title: i18nPackage.t("editQuizPage.configurationSummaryListTSX.compact.showTools"),
					layout: "sentence",
					i18nSentenceKey: "editQuizPage.configurationSummaryListTSX.compact.resourceSummary",
					items: items,
				});
			}
		}

		return groups;
	}, [
		allowedCategories,
		showCategoriesWithNoItems,
		summary.otherActivities,
		summary.record,
		summary.showTools,
		summary.verification,
	]);

	return (
		<div className={styles.compactListContainer}>
			{renderedGroups.map((category) => {
				const hasItems = category.items && category.items.length > 0;
				return (
					<ConfirmationBlock
						variant="compact"
						key={category.categoryKey}
						title={category.title}
						onEdit={onEdit ? () => onEdit(category.categoryKey) : undefined}
						targetPageName={category.categoryKey}
					>
						{hasItems ? (
							category.layout === "sentence" && category.i18nSentenceKey ? (
								<ConfirmationRowDescription>
									{renderBoldSentence(category.items, category.i18nSentenceKey)}
								</ConfirmationRowDescription>
							) : (
								<div className={styles.restrictionsGrid}>
									{category.items.map((item) => (
										<GridItemWithListPopover key={item.key} item={item} />
									))}
								</div>
							)
						) : (
							<ConfirmationRowDescription>
								{i18nPackage.t("editQuizPage.confirmationBlockTSX.noRestrictions")}
							</ConfirmationRowDescription>
						)}
					</ConfirmationBlock>
				);
			})}
		</div>
	);
}
